import sjcl from "sjcl";
import dayjs from "dayjs";
import { httpGet, httpPost, httpDelete, httpPut } from "./AbstractService";
import { getYYYYMMDD } from "./DateService";
import ENDPOINTS from "../data/endpoints.json";

const STATUS_APPROVED = 50;
const STATUS_RETURNED = 100;
const STATUS_ARRIVED = 200;
const STATUS_RETURNED_LATE = 110;
const STATUS_REJECTED = -100;
const STATUS_DID_NOT_ARRIVE = 400;
const STATUS_DID_NOT_RETURN = 500;
const STATUS_PAST_TIMEOUT = 700;

export const PASS_QR_KEY = "7iggJ0UIyM5dgj7yPYuMMbk2tXU6MF1j";

export const getIncomingTeacherPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/pass/incoming`);
};

export const getOpenTeacherPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/pass/open`);
};

export const getOpenStudentPases = (school, jwt, studentEid, date = null) => {
  let url = `${ENDPOINTS[school.region].pass}/student/${studentEid}/pass/open`;

  if (date != null) {
    url += `?date=${date}`;
  }
  return httpGet(jwt, url);
};

export const getStudentPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/student/pass`);
};

export const getPassTypes = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/pass_type`);
};

export const getPassType = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/pass_type/${eid}`);
};

export const adminGetPassTypeStudents = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].pass}/pass_type/${eid}/student`
  );
};

export const returnQrPass = (school, jwt, teacherEid, date) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/return/bathroom`, {
    teacherEid,
    date,
  });
};

export const createPass = (
  school,
  jwt,
  typeEid,
  dateStr,
  teacherEid,
  toLocationId,
  toTeacherEid,
  reason,
  teacherComment,
  approvalCode,
  qrPass
) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].pass}/pass`, {
    type_eid: typeEid,
    date: dateStr,
    teacher_eid: teacherEid,
    to_location_id: toLocationId,
    to_teacher_eid: toTeacherEid,
    reason: reason,
    teacher_comment: teacherComment,
    approval_code: approvalCode,
    qrPass,
  });
};

export const createPassByTeacher2 = (
  school,
  jwt,
  studentEid,
  typeEid,
  dDate,
  teacherEid,
  toLocationId,
  toTeacherEid,
  reason,
  teacherComment,
  approvalCode,
  qrPass
) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].pass}/pass`, {
    student_eids: [studentEid],
    type_eid: typeEid,
    date: getYYYYMMDD(dDate),
    teacher_eid: teacherEid,
    to_location_id: toLocationId,
    to_teacher_eid: toTeacherEid,
    reason: reason,
    teacher_comment: teacherComment,
    approval_code: approvalCode,
    qrPass,
  });
};

// time format: hh:mmA
export const createPassByTeacher = (school, jwt, values) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].pass}/pass`, values);
};

export const deletePass = (school, jwt, eid) => {
  return httpDelete(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`);
};

export const approvePass = (school, jwt, eid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_APPROVED,
  });
};

export const rejectPass = (school, jwt, eid, comment) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_REJECTED,
    teacherComment: comment,
  });
};

export const returnPass = (school, jwt, eid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_RETURNED,
  });
};

export const arrivedPass = (school, jwt, eid, comment) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_ARRIVED,
    comment
  });
};

export const didNotArrivePass = (school, jwt, eid, comment) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_DID_NOT_ARRIVE,
    comment
  });
};

export const returnLatePass = (school, jwt, eid, comment) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_RETURNED_LATE,
    teacherComment: comment,
  });
};

export const didNotReturnPass = (school, jwt, eid, comment) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    status: STATUS_DID_NOT_RETURN,
    teacherComment: comment,
  });
};

export const getBathroomPass = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/pass/leave/bathroom`);
};

export const encryptLeavePass = (key, locationEid, userEid, passTypeEid) => {
  const value = `${locationEid}/${userEid}/${passTypeEid}`;
  return btoa(sjcl.encrypt(key, value));
};

export const decryptLeavePass = (key, value) => {
  const decrypted = sjcl.decrypt(key, atob(value));
  const splitted = decrypted.split("/");
  return {
    locationEid: splitted[0],
    userEid: splitted[1],
    passTypeEid: splitted[2],
  };
};

export const encryptReturnPass = (key, userEid) => {
  const value = `${userEid}`;
  return btoa(sjcl.encrypt(key, value));
};

export const decryptReturnPass = (key, value) => {
  const decrypted = sjcl.decrypt(key, atob(value));
  const splitted = decrypted.split("/");
  return {
    userEid: splitted[0],
  };
};

export const getSchoolDidNotReturnPasses = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].pass}/school/pass?status=500`
  );
};

export const getAllStudentPasses = (school, jwt, studentEid) => {
  let url = `${ENDPOINTS[school.region].pass}/student/${studentEid}/pass/all`;
  return httpGet(jwt, url);
};

export const getPassStatusText = (status) => {
  switch (status) {
    case 0:
      return "Created";
    case 50:
      return "Approved";
    case 100:
      return "Returned";
    case 110:
      return "Returned Late";
    case -100:
      return "Rejected";
    case -200:
      return "Unprocessed";
    case 200:
      return "Defaulted";
    case 400:
      return "Did not Arrive";
    case 500:
      return "Did not Return";
    case 600:
      return "Defaulted";
    case 700:
      return "Past Timeout";
    default:
      return "idk";
  }
};

export const getOpenSchoolPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/school/pass/open`);
};

export const getPassLocations = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/school/pass/location`);
};

export const getPassNotifications = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/notify`);
};

export const adminAddPassNotifications = (school, jwt, teacherEids) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].pass}/notify`, {
    teacherEids,
  });
};

export const adminRemovePassNotification = (school, jwt, notificationEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/notify`, {
    notificationEid,
  });
};

export const adminPassGetExcludedStudents = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].pass}/school/pass/student/excluded`
  );
};

export const adminPassAddStudentsExcluded = (school, jwt, studentEids) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].pass}/school/pass/student/excluded`,
    {
      studentEids,
    }
  );
};

export const adminPassRemoveExcluded = (school, jwt, id) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].pass}/school/pass/student/excluded`,
    {
      id,
    }
  );
};

export const adminPassGetSettings = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/school/pass/settings`);
};

export const adminPassGetUpdateSettings = (
  school,
  jwt,
  enablePassBySchedule,
  passScheduleGracePeriod
) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/school/pass/settings`, {
    enablePassBySchedule,
    passScheduleGracePeriod,
  });
};

export const createBoomerang = (school, jwt, passEid) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].pass}/teacher/pass/boomerang`, {
    passEid,
  });
};

export const adminAddPassTypeStudents = (school, jwt, eid, studentEids) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].pass}/pass_type/${eid}/student`,
    {
      studentEids,
    }
  );
};

export const adminRemovePassTypeStudent = (school, jwt, passTypeEid, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].pass}/pass_type/${passTypeEid}/student/${id}`
  );
};

export const calcTimeRemain = (passes) => {
  for (let pass of passes) {
    const dateCurrent = dayjs();

    let dateTimeout = dayjs(pass.approved_at);
    dateTimeout = dateTimeout.add(pass.timeout, "minute");

    const dateTimeoutObj = dayjs(
      `2022-04-17T${dateTimeout.format("HH:mm:ss")}`
    );
    const dateCurrentObj = dayjs(
      `2022-04-17T${dateCurrent.format("HH:mm:ss")}`
    );

    console.log(dateTimeout.format("HH:mm:ss"));
    console.log(dateCurrent.format("HH:mm:ss"));

    let seconds = dateTimeoutObj.diff(dateCurrentObj, "second");
    // console.log(seconds);

    if (seconds < 0) {
      pass.timeOverageSeconds = Math.abs(seconds);
      seconds = 0;
      pass.status = STATUS_PAST_TIMEOUT;
    }
    pass.timeRemainSeconds = seconds;
  }
};

export const getOpenKioskPasses = (school, jwt, studentEid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].pass}/kiosk/student/${studentEid}/open`
  );
};


export const closeKioskUserPasses = (school, jwt, studentEid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].pass}/kiosk/student/${studentEid}/close`
  );
};

export const getBathroomPassTypes = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/pass_type?isBathroom=true`);
};

export const addPassMinute = (
  school,
  jwt,
  eid
) => {
  const minutes = 1;
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    "action": "add_minute",
    "minutes": minutes,
  });
};

export const resetTimeout = (
  school,
  jwt,
  eid
) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    "action": "reset_timeout",
  });
};

export const getTodaysTeacherPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/pass/today`);
};
export const getTodaysQueuedTeacherPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/pass/queued`);
};
export const getTodayAllTeacherPasses = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/pass/all`);
};
export const getTeacherLocations = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/location`);
};

export const updateTeacherLocation = (school, jwt, teacherEid, bathroomLocationId, defaultLocationId) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/teacher/${teacherEid}/location`, {
    bathroomLocationId,
    defaultLocationId,
  });
};

export const updateLocation = (
  school,
  jwt,
  eid,
  locationId
) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].pass}/pass/${eid}`, {
    "action": "update_location",
    "locationId": locationId,
  });
};

export const getTeacherDefaultLocations = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].pass}/teacher/default-location`);
};

